import { useIsFocused } from "expo-router/react-navigation";
import { router, useLocalSearchParams } from 'expo-router';
import { capitalize } from 'lodash';
import { useEffect, useLayoutEffect } from 'react';
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';
import { Divider } from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useCreateManyBoardDirectories } from '@/api/hooks/boardDirectoriesHooks';
import { useNpoOnboarding } from '@/api/hooks/meHooks';
import { NonProfitOrg } from '@/api/models/nonProfitOrgModels';
import AddAnotherButton from '@/components/buttons/AddAnotherButton';
import BrandedButton from '@/components/buttons/BrandedButton';
import DirectorForm from '@/components/forms/DirectorForm';
import FormWrapper from '@/components/forms/FormWrapper';
import FieldArrayListItemCard from '@/components/npo/onboarding/FieldArrayListItemCard';
import OnboardingSection from '@/components/npo/onboarding/OnboardingSection';
import SaveAndContinueLaterButton from '@/components/npo/onboarding/SaveAndContinueLaterButton';
import Text from '@/components/Text';
import { useAuth } from '@/contexts/authContext';
import { useNpoLoginScreenState } from '@/contexts/npoLoginScreenStateContext';
import { BoardOfDirectorsData, boardOfDirectorsResolver, DirectorData } from '@/lib/validations/NpoOnboardingSchema';
import { i18n } from '@/utils/i18n';
import { safeJsonParse } from '@/utils/jsonParse';
import { objectComparision } from '@/utils/objectComparision';

const BoardDirectors = () => {
  const { directorDataJson, directorIndex } = useLocalSearchParams<{
    directorDataJson: string;
    directorIndex: string;
  }>();
  const { state: authState } = useAuth();
  const { state: npoScreensState, setCurrentPage } = useNpoLoginScreenState();
  const { mutateAsync: onBoardingUpdate, isPending: onBoardingIsPending } = useNpoOnboarding(
    authState.signUpData?.loginEmail?.type ?? 'CHURCH'
  );
  const { mutateAsync: createManyBoardDirectories, isPending: boardDirectoriesPending } =
    useCreateManyBoardDirectories();

  const { height } = useWindowDimensions();
  const { top, bottom } = useSafeAreaInsets();

  const isFocused = useIsFocused();
  useLayoutEffect(() => {
    if (isFocused) authState.signUpData?.loginEmail?.type === 'CHURCH' ? setCurrentPage(4) : setCurrentPage(5);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused]);

  useEffect(() => {
    if (directorDataJson) {
      const directorData = safeJsonParse<DirectorData>(directorDataJson);
      if (directorData && fields !== undefined) {
        // eslint-disable-next-line no-unused-expressions
        !directorIndex && !fields.find((field) => objectComparision(field, directorData))
          ? append(directorData)
          : update(+directorIndex, directorData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directorDataJson]);

  const nonProfitOrg = safeJsonParse<NonProfitOrg>(npoScreensState.initialNpoData ?? '');

  const isIndividual = authState.authReady && authState.user?.user.role === 'INDIVIDUAL';
  const isChurch = authState.signUpData?.loginEmail?.type === 'CHURCH';

  const {
    control,
    handleSubmit,
    trigger,
    clearErrors,
    getValues,
    getFieldState,
    formState: { isValid, submitCount, errors }
  } = useForm<BoardOfDirectorsData>({
    defaultValues: {
      boardDirectories:
        nonProfitOrg?.usersBoardDirectory !== undefined
          ? safeJsonParse<DirectorData[]>(JSON.stringify(nonProfitOrg.usersBoardDirectory))
          : []
    },
    mode: 'onSubmit',
    resolver: boardOfDirectorsResolver,
    context: { isIndividual, isChurch }
  });

  const { fields, insert, append, update, remove } = useFieldArray({
    control,
    name: 'boardDirectories'
  });

  const onSubmit: SubmitHandler<BoardOfDirectorsData> = async (data) => {
    const mappedData = data.boardDirectories.map((director) => ({
      ...director,
      nonProfitOrgId: authState.signUpData?.loginEmail?.npoId ?? ''
    }));
    const body = {
      boardDirectories: mappedData,
      npoId: authState.signUpData?.loginEmail?.npoId
    };

    try {
      createManyBoardDirectories(body, {
        onSuccess: () => {
          onBoardingUpdate(
            {
              preferenceId: authState.preferenceId ?? '',
              boardDirectors: true
            },
            {
              onSuccess: () => {
                if (authState.signUpData?.loginEmail?.type === 'CHURCH') {
                  router.navigate('/(app)/login/nonProfit/onBoarding/pastors');
                } else {
                  router.navigate('/(app)/login/nonProfit/onBoarding/totalSalaries');
                }
              }
            }
          );
        },
        onError: (e) => {
          console.error(e);
        }
      });
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <FormWrapper scrollEnable minHeightMinus={160}>
      {/* Height - Header */}
      <View style={styles.container}>
        <OnboardingSection
          label={i18n.t('sign_in.nonProfit.board_directors.title')}
          description={i18n.t('sign_in.nonProfit.board_directors.description')}>
          {getValues('boardDirectories') && getValues('boardDirectories').length === 0 ? (
            // height - header - footer
            <View style={{ height: height - 160 - bottom - top }}>
              <DirectorForm
                initialData={getValues('boardDirectories.0')}
                showDoneButton
                showBackButton
                isLoading={onBoardingIsPending || boardDirectoriesPending}
                manualValidation={isChurch}
                onDone={(data, triggerDirector) => {
                  if (!data.fullName && !data.position && !getFieldState('boardDirectories').isDirty) {
                    if (isChurch) {
                      clearErrors();
                      handleSubmit(onSubmit)();
                    }
                    return;
                  }
                  if (data.fullName && data.position) {
                    clearErrors();
                    insert(0, data);
                    handleSubmit(onSubmit)();
                  } else if (!data.fullName || !data.position || data.allowance || data.annualSalary) {
                    triggerDirector();
                  }
                }}
                onAdd={(data) => {
                  clearErrors();
                  update(0, data);
                  trigger('boardDirectories.0').then((isValid) => {
                    if (isValid) {
                      router.navigate('/login/nonProfit/addUpdateDirector');
                    }
                  });
                }}
                doneLabel={i18n.t('mix.next')}
              />
            </View>
          ) : (
            <View style={styles.flexOne}>
              <Divider style={styles.divider} />
              <FlatList
                style={styles.flexOne}
                contentContainerStyle={styles.flexOne}
                scrollEnabled={false}
                data={fields}
                keyExtractor={(director) => director.id}
                renderItem={(field) => (
                  <FieldArrayListItemCard
                    onPress={() => {
                      router.navigate({
                        pathname: '/login/nonProfit/addUpdateDirector',
                        params: {
                          directorDataJson: JSON.stringify(field.item),
                          directorIndex: field.index
                        }
                      });
                    }}
                    onDelete={() => {
                      remove(field.index);
                    }}
                    title={field.item.fullName}
                    subtitle={field.item.position ?? ''}
                    budget={field.item.annualSalary}
                  />
                )}
                ListFooterComponent={
                  <AddAnotherButton
                    onPress={() => router.navigate('/login/nonProfit/addUpdateDirector')}
                    label={i18n.t('sign_in.nonProfit.board_directors.add_another')}
                  />
                }
              />
            </View>
          )}
        </OnboardingSection>

        {errors.root && <Text style={styles.errorText}>{errors?.root?.message}</Text>}

        <View style={styles.buttonsContainer}>
          {getValues('boardDirectories') && getValues('boardDirectories').length > 0 && (
            <View style={styles.nextBackContainer}>
              {router.canGoBack() && (
                <BrandedButton
                  disabled={onBoardingIsPending || boardDirectoriesPending}
                  style={styles.flexOne}
                  onPress={router.back}>
                  {capitalize(i18n.t('mix.back'))}
                </BrandedButton>
              )}
              <BrandedButton
                style={styles.flexOne}
                loading={onBoardingIsPending || boardDirectoriesPending}
                disabled={!isValid && submitCount > 0}
                onPress={handleSubmit(onSubmit)}>
                {i18n.t('mix.next')}
              </BrandedButton>
            </View>
          )}
          <SaveAndContinueLaterButton disabled={onBoardingIsPending || boardDirectoriesPending} />
        </View>
      </View>
    </FormWrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between'
  },

  buttonsContainer: {
    flexDirection: 'column'
  },

  flexOne: {
    flex: 1
  },

  nextBackContainer: {
    flex: 1,
    flexDirection: 'row',
    width: '100%',
    gap: 10
  },

  errorText: {
    color: 'red',
    fontSize: 20,
    fontWeight: 'bold'
  },

  divider: {
    marginBottom: 15
  }
});

export default BoardDirectors;
